/**
 * ScrollTracker - die Rohmessung.
 *
 * Aufgabe: aus nativen Scroll-Events eine verlaessliche, in Zentimetern
 * ausgedrueckte Wegstrecke machen. Alles darueber (Disziplin-Regeln, Anzeige)
 * liegt bewusst woanders; diese Klasse kennt keine Wettkampfregeln.
 *
 * Drei Entscheidungen, die den Rest der App praegen:
 *
 * 1. **Nur vorwaerts zaehlt.** Gezaehlt werden ausschliesslich Bewegungen nach
 *    unten. Zurueckscrollen bringt nichts. Das entspricht dem Laufen auf einer
 *    Bahn und nimmt der billigsten Betrugsmasche - dem Hin-und-her-Ruetteln -
 *    die Grundlage.
 *
 * 2. **Nachrollen zaehlt mit.** Gemessen wird die Bewegung des Inhalts, nicht
 *    die des Fingers. Das Ausrollen nach dem Loslassen ist Teil der Strecke;
 *    ohne das gaebe es die Disziplin Sprung nicht.
 *
 * 3. **Die Bahn ist endlich, wirkt aber unendlich.** Ein Browser begrenzt die
 *    Hoehe eines Elements. Naehert sich der Nutzer dem Ende, springt die Bahn
 *    unsichtbar zurueck und der zurueckgelegte Weg wird in wrapOffset gerettet.
 *    Der Sprung passiert nur, wenn nichts in Bewegung ist - ein Umsetzen von
 *    scrollTop wuerde auf iOS sonst das Nachrollen abwuergen und die Messung
 *    verfaelschen.
 */

import type { DeviceMetrics } from './dpi'
import { cmPerCssPx } from './units'

/**
 * Hoehe der Scroll-Bahn in CSS-Pixeln.
 *
 * Browser deckeln die Hoehe eines Elements (Firefox am strengsten bei rund
 * 17,8 Mio. px). 12 Mio. liegen sicher darunter und entsprechen auf einem
 * typischen Handy gut zwei Kilometern am Stueck. Die tatsaechlich zugestandene
 * Hoehe wird beim Start nachgemessen, statt ihr zu vertrauen.
 */
export const TRACK_HEIGHT_PX = 12_000_000

/** Startposition auf der Bahn. Laesst etwas Raum zum Zurueckscrollen. */
const START_OFFSET_PX = 100_000

/** Ab diesem Restweg bis zum Bahnende wird zurueckgesetzt. */
const WRAP_MARGIN_PX = 500_000

/** Ohne Scroll-Event fuer diese Dauer gilt die Bewegung als beendet. */
export const SETTLE_MS = 140

/** Abstand der Stuetzpunkte fuer die Serverpruefung. */
const SAMPLE_INTERVAL_MS = 50

/** Obergrenze der Stuetzpunkte, damit ein Marathonlauf die Anfrage nicht sprengt. */
const MAX_SAMPLES = 600

export interface TrackerSample {
  /** Millisekunden seit Messbeginn. */
  t: number
  /** Vorwaerts zurueckgelegter Weg in Zentimetern zu diesem Zeitpunkt. */
  cm: number
}

export interface TrackerEvents {
  /** Bei jeder gemessenen Vorwaertsbewegung. */
  onProgress?: (forwardCm: number, deltaCm: number) => void
  /** Finger beruehrt den Bildschirm. */
  onTouchStart?: () => void
  /** Finger verlaesst den Bildschirm; das Nachrollen beginnt. */
  onTouchEnd?: () => void
  /** Alles steht still: kein Finger, kein Nachrollen. */
  onSettle?: (forwardCm: number) => void
}

export class ScrollTracker {
  private readonly el: HTMLElement
  private readonly cmPerPx: number
  private events: TrackerEvents

  /** In frueheren Bahn-Durchlaeufen gesammelter Weg, in CSS-Pixeln. */
  private wrapOffset = 0
  private lastTop = 0
  private maxScrollTop = 0

  /** Vorwaerts zurueckgelegter Weg seit dem letzten reset(), in CSS-Pixeln. */
  private forwardPx = 0

  private startedAt: number | null = null
  private lastEventAt = 0
  private lastVelocityAt = 0
  private velocityPxPerMs = 0

  private touching = false
  private settleTimer: number | null = null

  private samples: TrackerSample[] = []
  private lastSampleAt = 0

  private destroyed = false

  constructor(element: HTMLElement, metrics: DeviceMetrics, events: TrackerEvents = {}) {
    this.el = element
    this.cmPerPx = cmPerCssPx(metrics.dpr, metrics.ppi)
    this.events = events

    this.el.addEventListener('scroll', this.handleScroll, { passive: true })
    this.el.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.el.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    this.el.addEventListener('touchcancel', this.handleTouchEnd, { passive: true })

    this.measureTrack()
    this.jumpTo(START_OFFSET_PX)
  }

  // --- Oeffentliche Messwerte --------------------------------------------

  /** Vorwaerts zurueckgelegter Weg in Zentimetern. */
  get forwardCm(): number {
    return this.forwardPx * this.cmPerPx
  }

  /** Aktuelle Geschwindigkeit in Zentimetern pro Sekunde. */
  get velocityCmPerSec(): number {
    return Math.abs(this.velocityPxPerMs) * 1000 * this.cmPerPx
  }

  /** Bewegt sich gerade etwas - durch Finger oder durch Nachrollen? */
  get isMoving(): boolean {
    return performance.now() - this.lastEventAt < SETTLE_MS
  }

  get isTouching(): boolean {
    return this.touching
  }

  /** Millisekunden seit der ersten gemessenen Bewegung, 0 vor dem Start. */
  get elapsedMs(): number {
    return this.startedAt === null ? 0 : performance.now() - this.startedAt
  }

  /** Absolute Position auf der gedachten unendlichen Bahn, in CSS-Pixeln. */
  get absolutePx(): number {
    return this.wrapOffset + this.el.scrollTop
  }

  /** Stuetzpunkte fuer die Plausibilitaetspruefung auf dem Server. */
  getSamples(): TrackerSample[] {
    return this.samples.slice()
  }

  setEvents(events: TrackerEvents): void {
    this.events = events
  }

  /** Setzt die Messung zurueck, ohne die Bahn zu verlassen. */
  reset(): void {
    this.forwardPx = 0
    this.startedAt = null
    this.velocityPxPerMs = 0
    this.samples = []
    this.lastSampleAt = 0
    this.clearSettleTimer()
    this.jumpTo(START_OFFSET_PX)
  }

  destroy(): void {
    this.destroyed = true
    this.clearSettleTimer()
    this.el.removeEventListener('scroll', this.handleScroll)
    this.el.removeEventListener('touchstart', this.handleTouchStart)
    this.el.removeEventListener('touchend', this.handleTouchEnd)
    this.el.removeEventListener('touchcancel', this.handleTouchEnd)
  }

  // --- Ereignisbehandlung -------------------------------------------------

  private handleScroll = (): void => {
    if (this.destroyed) return

    const now = performance.now()
    const top = this.el.scrollTop
    const delta = top - this.lastTop
    this.lastTop = top

    this.updateVelocity(delta, now)
    this.lastEventAt = now

    if (delta > 0) {
      // Nur Vorwaertsbewegung zaehlt.
      if (this.startedAt === null) this.startedAt = now
      this.forwardPx += delta
      this.recordSample(now)
      this.events.onProgress?.(this.forwardCm, delta * this.cmPerPx)
    }

    this.armSettleTimer()
    this.maybeWrap()
  }

  private handleTouchStart = (): void => {
    if (this.destroyed) return
    this.touching = true
    this.lastEventAt = performance.now()
    this.clearSettleTimer()
    // Der Finger auf dem Glas stoppt jedes Nachrollen. Genau jetzt ist das
    // Zuruecksetzen der Bahn unschaedlich.
    this.maybeWrap(true)
    this.events.onTouchStart?.()
  }

  private handleTouchEnd = (): void => {
    if (this.destroyed) return
    this.touching = false
    this.lastEventAt = performance.now()
    this.armSettleTimer()
    this.events.onTouchEnd?.()
  }

  /**
   * Geschwindigkeit als exponentiell geglaetteter Mittelwert. Rohe Einzeldeltas
   * schwanken zwischen zwei Frames zu stark, um sie anzuzeigen oder als
   * Stillstandskriterium zu benutzen.
   */
  private updateVelocity(deltaPx: number, now: number): void {
    const dt = now - this.lastVelocityAt
    this.lastVelocityAt = now
    if (dt <= 0 || dt > 200) {
      // Erster Event oder lange Pause: kein sinnvoller Bezugswert.
      this.velocityPxPerMs = 0
      return
    }
    const instant = deltaPx / dt
    const smoothing = 0.3
    this.velocityPxPerMs = this.velocityPxPerMs * (1 - smoothing) + instant * smoothing
  }

  private recordSample(now: number): void {
    if (this.startedAt === null) return
    if (now - this.lastSampleAt < SAMPLE_INTERVAL_MS) return
    this.lastSampleAt = now
    if (this.samples.length >= MAX_SAMPLES) {
      // Aelteste Haelfte ausduennen statt abschneiden: die Kurve bleibt ueber
      // die volle Laufzeit erkennbar, nur grober aufgeloest.
      this.samples = this.samples.filter((_, i) => i % 2 === 0)
    }
    this.samples.push({
      t: Math.round(now - this.startedAt),
      cm: Number(this.forwardCm.toFixed(2)),
    })
  }

  private armSettleTimer(): void {
    this.clearSettleTimer()
    this.settleTimer = window.setTimeout(() => {
      this.settleTimer = null
      if (this.touching || this.destroyed) return
      this.velocityPxPerMs = 0
      this.events.onSettle?.(this.forwardCm)
    }, SETTLE_MS)
  }

  private clearSettleTimer(): void {
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer)
      this.settleTimer = null
    }
  }

  // --- Bahnverwaltung -----------------------------------------------------

  private measureTrack(): void {
    // Nicht der angeforderten Hoehe vertrauen: Browser deckeln sie
    // unterschiedlich streng, und ein zu grosser Wert wuerde stillschweigend
    // gekappt.
    this.maxScrollTop = Math.max(0, this.el.scrollHeight - this.el.clientHeight)
  }

  /**
   * Setzt die Bahn zurueck, wenn das Ende naht.
   *
   * @param force Auch dann zuruecksetzen, wenn noch Bewegung im Spiel ist -
   *   zulaessig ausschliesslich bei aufgesetztem Finger, weil der das
   *   Nachrollen ohnehin beendet.
   */
  private maybeWrap(force = false): void {
    if (this.maxScrollTop <= 0) this.measureTrack()
    if (this.el.scrollTop < this.maxScrollTop - WRAP_MARGIN_PX) return
    if (!force && this.isMoving) return
    this.jumpTo(START_OFFSET_PX)
  }

  /**
   * Springt an eine Position der Bahn, ohne dass der Sprung als zurueckgelegter
   * Weg zaehlt. Der uebersprungene Betrag wandert in wrapOffset, damit die
   * absolute Position ueber alle Durchlaeufe hinweg stimmig bleibt.
   */
  private jumpTo(target: number): void {
    const before = this.el.scrollTop
    this.el.scrollTop = target
    const after = this.el.scrollTop
    this.wrapOffset += before - after
    this.lastTop = after
  }
}
